import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";

import { TemplateField } from "@/shared/components/TemplateField";
import { Checkbox, Field, FormBlock } from "@/shared/foundation/primitives";

import {
  IMPLICIT_COLLECTION_PLACEHOLDER,
  IMPLICIT_COLLECTION_DESCRIPTION,
} from "../../Agents/buildRequestTemplate";
import {
  MAX_EVENT_VOCABULARY,
  MAX_TAG_VOCABULARY,
} from "../../Agents/goals/validation";
import { type AgentFormValues } from "../agentFormSchema";
import { useAgentVariableCatalog } from "../useAgentVariableCatalog";

import styles from "./goals.module.css";
import { ImplicitBinding } from "./ImplicitBinding";
import { VocabularyBlock } from "./VocabularyBlock";

export interface CreateEventsGoalFormProps {
  index: number;
}

/**
 * What a create-events goal needs: the kinds of event it may create, the tags
 * it may attach, whether the results are collected, and any guidance layered
 * on top of those vocabularies.
 */
export function CreateEventsGoalForm({ index }: CreateEventsGoalFormProps) {
  const { control } = useFormContext<AgentFormValues>();
  const catalog = useAgentVariableCatalog();
  const focusTitleId = React.useId();

  return (
    <>
      <VocabularyBlock
        name={`goals.${index}.event_vocabulary`}
        keyField="name"
        title="Event types"
        hint={
          <>
            The kinds of event the agent may create; it can create no others.
            Reference a variable in a description with <code>{"{{name}}"}</code>
            .
          </>
        }
        keyHeading="Event type"
        keyPlaceholder="hard-brake"
        descriptionHeading="What it captures"
        descriptionPlaceholder="A rapid, sustained decrease in vehicle speed."
        addLabel="Add event type"
        max={MAX_EVENT_VOCABULARY}
        testId={`agent-goal-${index}-events`}
      />

      <VocabularyBlock
        name={`goals.${index}.tag_vocabulary`}
        keyField="tag"
        title="Tags"
        hint="Tags the agent may attach to an event it creates; the description says when each one applies. Leave empty to create events without tags."
        keyHeading="Tag"
        keyPlaceholder="needs-review"
        descriptionHeading="When it applies"
        descriptionPlaceholder="Apply when the event warrants a human look."
        addLabel="Add tag"
        max={MAX_TAG_VOCABULARY}
        testId={`agent-goal-${index}-tags`}
      />

      <Controller
        control={control}
        name={`goals.${index}.collection_enabled`}
        render={({ field }) => (
          <div
            className={styles.block}
            data-testid={`agent-goal-${index}-collection`}
            data-enabled={field.value === true}
          >
            <Checkbox
              checked={field.value === true}
              onCheckedChange={field.onChange}
              name={field.name}
              size="md"
              label="Add created events to a collection"
              description="The launch page then asks for an event collection, and every created event is added to it."
            />
            {field.value === true && (
              <ImplicitBinding
                label="Collection"
                placeholder={IMPLICIT_COLLECTION_PLACEHOLDER}
                hint={IMPLICIT_COLLECTION_DESCRIPTION}
              />
            )}
          </div>
        )}
      />

      <FormBlock
        titleId={focusTitleId}
        title="Event focus"
        hint="Guidance layered on top of the vocabularies — “only flag intervals longer than five seconds.”"
        className={styles.block}
        data-testid={`agent-goal-${index}-focus`}
      >
        <Controller
          control={control}
          name={`goals.${index}.event_focus_prompt`}
          render={({ field, fieldState }) => (
            <Field.Root invalid={fieldState.invalid}>
              <TemplateField
                value={typeof field.value === "string" ? field.value : ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                catalog={catalog}
                labelledBy={focusTitleId}
                rows={4}
                error={fieldState.invalid}
                placeholder="Only flag intervals the on-board QA pass did not already catch."
              />
              <Field.Error match>{fieldState.error?.message}</Field.Error>
            </Field.Root>
          )}
        />
      </FormBlock>
    </>
  );
}
